import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import LayoutMobileOverridesPanel from './LayoutMobileOverridesPanel';
import '../css/ButtonConfig.css';
import { IconPickerModal } from './BuilderIconPickerModal';
import ColorPaletteDock from './ColorPaletteDock';

const ToggleSwitch = ({ label, value, onChange }) => (
    <div className="bc-form-group">
        <div className="bc-toggle-wrapper">
            <span className="bc-label" style={{ marginBottom: 0, flex: 1 }}>{label}</span>
            <div style={{ display: 'flex', gap: '4px' }}>
                <button className={`bc-toggle-btn yes ${value ? 'active' : ''}`} onClick={() => onChange(true)}>YES</button>
                <button className={`bc-toggle-btn no ${!value ? 'active' : ''}`} onClick={() => onChange(false)}>NO</button>
            </div>
        </div>
    </div>
);

const SliderControl = ({ label, value, onChange, min = 0, max = 100, unit = 'px' }) => (
    <div className="bc-slider-row">
        <div className="bc-slider-label">{label}</div>
        <input type="range" className="bc-slider" min={min} max={max} value={value || 0} onChange={e => onChange(e.target.value)} />
        <input type="number" className="bc-slider-value" value={value || 0} onChange={e => onChange(e.target.value)} />
        <span className="bc-slider-unit">{unit}</span>
    </div>
);

const CollapsibleSection = ({ id, title, expandedId, onToggle, children }) => {
    const isOpen = expandedId === id;
    return (
        <div className="bc-section-panel">
            <button
                className={`bc-section-btn${isOpen ? ' open' : ''}`}
                onClick={() => onToggle(isOpen ? null : id)}
            >
                <span>{title}</span>
                <span className="bc-section-chevron">▼</span>
            </button>
            {isOpen && (
                <div className="bc-section-content">
                    {children}
                </div>
            )}
        </div>
    );
};

const ColorRow = ({ label, value, onChange }) => (
    <div className="bc-color-row">
        <div className="bc-color-label">{label}</div>
        <div className="bc-color-checkerboard" style={{ position: 'relative' }}>
            <div className="bc-color-fill" style={{ backgroundColor: value }}></div>
            <input type="color" value={value === 'transparent' ? '#ffffff' : (value || '#000000')} onChange={e => onChange(e.target.value)} style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer', position: 'absolute', top: 0, left: 0 }} />
        </div>
        <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} className="bc-input" style={{ width: '100px', flex: 'none', fontFamily: 'monospace' }} />
    </div>
);

const ButtonConfigModal = ({ initialContent, onSave, onChange, onClose, currentPosition, totalCount, onPositionChange }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(t('builder:general'));
    const [showIconPicker, setShowIconPicker] = useState(false);

    const [originalContent] = useState(() => JSON.parse(JSON.stringify({
        text: 'Clique Aqui',
        url: '',
        openInNewTab: false,
        size: 'md',
        shape: 'rounded',
        align: 'center',
        width: 100,
        fontWeight: 'Global',
        letterSpacing: 0,
        wordSpacing: 0,
        uppercase: false,
        colorsNormal: { text: '#ffffff', bg: '#3b82f6', border: 'transparent' },
        colorsHover: { text: '#ffffff', bg: '#2563eb', border: 'transparent' },
        marginObj: { top: 0, bottom: 0, left: 0, right: 0 },
        customClass: '',
        ...initialContent
    })));

    // Estado inicial padrão e mesclagem
    const [content, setContent] = useState({
        text: 'Clique Aqui',
        url: '',
        openInNewTab: false,
        size: 'md', // sm, md, lg, xl, full
        shape: 'rounded', // square, rounded, pill
        align: 'center', // left, center, right
        width: 100,
        fontWeight: 'Global',
        letterSpacing: 0,
        wordSpacing: 0,
        uppercase: false,
        colorsNormal: { text: '#ffffff', bg: '#3b82f6', border: 'transparent' },
        colorsHover: { text: '#ffffff', bg: '#2563eb', border: 'transparent' },
        marginObj: { top: 0, bottom: 0, left: 0, right: 0 },
        customClass: '',
        ...initialContent
    });

    const [expandedColors, setExpandedColors] = useState(null);
    const [expandedStyle, setExpandedStyle] = useState(null);

    const triggerChange = (newContent) => {
        setContent(newContent);
        if (onChange) onChange(newContent);
    };

    const update = (field, value) => triggerChange({ ...content, [field]: value });
    const updateNormalColor = (field, val) => triggerChange({ ...content, colorsNormal: { ...content.colorsNormal, [field]: val } });
    const updateHoverColor = (field, val) => triggerChange({ ...content, colorsHover: { ...content.colorsHover, [field]: val } });
    const updateMargin = (side, val) => triggerChange({ ...content, marginObj: { ...content.marginObj, [side]: parseInt(val) || 0 } });

    const buttonIconAutoPx = () => {
        const sz = content.size;
        if (sz === 'S') return 12;
        if (sz === 'L') return 16;
        if (sz === 'XL') return 18;
        return 14;
    };
    const buttonIconPxDisplayed = Number.isFinite(content.buttonIconSizePx) && content.buttonIconSizePx >= 12
        ? Math.min(48, Math.max(12, content.buttonIconSizePx))
        : buttonIconAutoPx();

    // Snapshot pro Undo caso cancele
    const handleCancel = () => {
        if (onChange) onChange(originalContent);
        onClose();
    };

    // ===== Lógica de Drag and Drop Customizada (Sem Refs Externos) =====
    const [modalPos, setModalPos] = useState({ x: window.innerWidth / 2 - 185, y: window.innerHeight / 2 - 250 }); // Centralizado inicialmente
    const draggingRef = React.useRef({ isDragging: false, startX: 0, startY: 0, initialX: 0, initialY: 0 });

    // ===== Resize do Modal (arrastar pelo canto) =====
    const MIN_W = 320; const MIN_H = 280; const MAX_W = 0.92 * window.innerWidth; const MAX_H = 0.9 * window.innerHeight;
    const [modalSize, setModalSize] = useState({ w: 370, h: Math.min(500, 0.85 * window.innerHeight) });
    const resizeRef = React.useRef({ active: false, startX: 0, startY: 0, startW: 0, startH: 0 });

    const handleResizeStart = (e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        resizeRef.current = { active: true, startX: e.clientX, startY: e.clientY, startW: modalSize.w, startH: modalSize.h };
        window.addEventListener('pointermove', handleResizeMove);
        window.addEventListener('pointerup', handleResizeEnd);
    };
    const handleResizeMove = (e) => {
        if (!resizeRef.current.active) return;
        const dw = e.clientX - resizeRef.current.startX;
        const dh = e.clientY - resizeRef.current.startY;
        setModalSize({
            w: Math.max(MIN_W, Math.min(MAX_W, resizeRef.current.startW + dw)),
            h: Math.max(MIN_H, Math.min(MAX_H, resizeRef.current.startH + dh))
        });
    };
    const handleResizeEnd = () => {
        resizeRef.current.active = false;
        window.removeEventListener('pointermove', handleResizeMove);
        window.removeEventListener('pointerup', handleResizeEnd);
    };

    const handlePointerDown = (e) => {
        // Apenas ativar na barra se for o botão esquerdo
        if (e.button !== 0) return;

        draggingRef.current.isDragging = true;
        draggingRef.current.startX = e.clientX;
        draggingRef.current.startY = e.clientY;
        draggingRef.current.initialX = modalPos.x;
        draggingRef.current.initialY = modalPos.y;

        // Atacha listeners na Window temporariamente para um arrasto contínuo e solto
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
    };

    const handlePointerMove = (e) => {
        if (!draggingRef.current.isDragging) return;
        e.preventDefault();

        const deltaX = e.clientX - draggingRef.current.startX;
        const deltaY = e.clientY - draggingRef.current.startY;

        setModalPos({
            x: draggingRef.current.initialX + deltaX,
            y: draggingRef.current.initialY + deltaY
        });
    };

    const handlePointerUp = () => {
        draggingRef.current.isDragging = false;
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
    };
    // ===================================

    const modalContent = (
        <div className="button-config-overlay" style={{ position: 'fixed', inset: 0, zIndex: 2147483647, pointerEvents: 'auto', isolation: 'isolate' }}>
            {showIconPicker && (
                <IconPickerModal t={t} onSelect={(icon) => { update('icon', icon); setShowIconPicker(false); }} onClose={() => setShowIconPicker(false)} />
            )}
            <div
                className="button-config-modal bc-modal-resizable"
                style={{
                    transform: `translate(${modalPos.x}px, ${modalPos.y}px)`,
                    left: 0,
                    top: 0,
                    width: modalSize.w,
                    height: modalSize.h,
                    minWidth: MIN_W,
                    minHeight: MIN_H,
                    maxWidth: MAX_W,
                    maxHeight: MAX_H,
                    '--bc-modal-h': `${modalSize.h}px`,
                }}
            >
                {/* HEADER - Arrastável */}
                <div className="bc-header" onPointerDown={handlePointerDown}>
                    <h3>{t('builder:elButton')}</h3>
                    <button className="bc-header-close" onPointerDown={(e) => e.stopPropagation()} onClick={onClose}>✖</button>
                </div>

                {/* TABS */}
                <div className="bc-tabs">
                    {[t('builder:general'), 'Type', 'Typography', 'Colors', 'Style'].map(tab => (
                        <button key={tab} className={`bc-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                            {tab}
                        </button>
                    ))}
                </div>

                {/* BODY */}
                <div className="bc-body">
                    {activeTab === t('builder:general') && (
                        <div>
                            {currentPosition != null && totalCount != null && onPositionChange && totalCount > 1 && (
                                <div className="bc-form-group" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                                    <label className="bc-label">{t('builder:stagePosition')}</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                        <input
                                            type="number"
                                            className="bc-input"
                                            min={1}
                                            max={totalCount}
                                            value={currentPosition}
                                            onChange={e => {
                                                const v = parseInt(e.target.value, 10);
                                                if (!isNaN(v) && v >= 1 && v <= totalCount) onPositionChange(v);
                                            }}
                                            style={{ width: '70px', textAlign: 'center' }}
                                        />
                                        <span className="bc-hint" style={{ margin: 0 }}>{t('builder:stagePositionHint')}</span>
                                        <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                                            <button
                                                type="button"
                                                disabled={currentPosition <= 1}
                                                onClick={() => onPositionChange(Math.max(1, currentPosition - 1))}
                                                style={{ padding: '4px 10px', fontSize: '0.85rem', border: '1px solid #cbd5e1', borderRadius: '4px', background: currentPosition <= 1 ? '#f1f5f9' : '#fff', cursor: currentPosition <= 1 ? 'not-allowed' : 'pointer', color: '#475569' }}
                                            >↑</button>
                                            <button
                                                type="button"
                                                disabled={currentPosition >= totalCount}
                                                onClick={() => onPositionChange(Math.min(totalCount, currentPosition + 1))}
                                                style={{ padding: '4px 10px', fontSize: '0.85rem', border: '1px solid #cbd5e1', borderRadius: '4px', background: currentPosition >= totalCount ? '#f1f5f9' : '#fff', cursor: currentPosition >= totalCount ? 'not-allowed' : 'pointer', color: '#475569' }}
                                            >↓</button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="bc-form-group">
                                <label className="bc-label">Text</label>
                                <input type="text" className="bc-input" value={content.text} onChange={e => update('text', e.target.value)} placeholder="Read more" />
                            </div>
                            <div className="bc-form-group">
                                <label className="bc-label">Link</label>
                                <input type="text" className="bc-input" value={content.link} onChange={e => update('link', e.target.value)} placeholder="#" />
                            </div>
                            <div className="bc-form-group">
                                <label className="bc-label">Icon</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <ToggleSwitch label="" value={content.showIcon} onChange={v => update('showIcon', v)} />
                                    {content.showIcon && (
                                        <button
                                            onClick={() => setShowIconPicker(true)}
                                            style={{ flex: 1, padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#f8fafc', color: '#334155', cursor: 'pointer', fontSize: '14px' }}
                                        >
                                            {(!content.icon || content.icon === 'default') ? t('builder:chooseIcon') : `${t('builder:iconLabel')} (${content.icon})`}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <ToggleSwitch label="Open link in a new window" value={content.newWindow} onChange={v => update('newWindow', v)} />
                        </div>
                    )}

                    {activeTab === 'Type' && (
                        <div>
                            <div className="bc-form-group">
                                <label className="bc-label">Type</label>
                                <select className="bc-input" value={content.type} onChange={e => {
                                    const newType = e.target.value;
                                    let newProps = { type: newType };
                                    if (newType === 'Primary') {
                                        newProps.border = false;
                                        newProps.colorsNormal = { text: '#ffffff', bg: '#3b82f6', border: 'transparent' };
                                        newProps.colorsHover = { text: '#ffffff', bg: '#2563eb', border: 'transparent' };
                                    } else if (newType === 'Secondary') {
                                        newProps.border = false;
                                        newProps.colorsNormal = { text: '#ffffff', bg: '#64748b', border: 'transparent' };
                                        newProps.colorsHover = { text: '#ffffff', bg: '#475569', border: 'transparent' };
                                    } else if (newType === 'Outline') {
                                        newProps.border = true;
                                        newProps.colorsNormal = { text: '#3b82f6', bg: '#3b82f6', border: '#3b82f6' };
                                        newProps.colorsHover = { text: '#ffffff', bg: '#3b82f6', border: '#3b82f6' };
                                    } else if (newType === 'Flat') {
                                        newProps.border = false;
                                        newProps.colorsNormal = { text: '#3b82f6', bg: 'transparent', border: 'transparent' };
                                        newProps.colorsHover = { text: '#2563eb', bg: '#f8fafc', border: 'transparent' };
                                    }
                                    triggerChange({ ...content, ...newProps });
                                }}>
                                    <option>Primary</option>
                                    <option>Secondary</option>
                                    <option>Outline</option>
                                    <option>Flat</option>
                                </select>
                            </div>
                            <div className="bc-form-group">
                                <label className="bc-label">Size</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {['S', 'M', 'L', 'XL'].map(s => (
                                        <button key={s} onClick={() => update('size', s)} style={{ flex: 1, padding: '8px', border: content.size === s ? '2px solid #f97316' : '1px solid #cbd5e1', background: content.size === s ? '#fff7ed' : '#f8fafc', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, color: content.size === s ? '#ea580c' : '#475569' }}>{s}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="bc-form-group">
                                <label className="bc-label">Rounded</label>
                                <select className="bc-input" value={content.rounded} onChange={e => update('rounded', e.target.value)}>
                                    <option>Global</option>
                                    <option>Yes (Pill)</option>
                                    <option>No (Square)</option>
                                </select>
                            </div>
                            <ToggleSwitch label="Modo Vazado (Outline)" value={content.border} onChange={v => update('border', v)} />
                            <ToggleSwitch label="Full width" value={content.fullWidth} onChange={v => update('fullWidth', v)} />
                            <ToggleSwitch label="Center" value={content.center} onChange={v => update('center', v)} />

                            {!content.fullWidth && (
                                <div style={{ marginTop: '20px' }}>
                                    <SliderControl label="Width" value={content.width} onChange={v => update('width', v)} max={100} unit="%" />
                                </div>
                            )}
                            {content.showIcon && (
                                <div className="bc-form-group" style={{ marginTop: '16px' }}>
                                    <SliderControl
                                        label={t('builder:buttonStyledIconSize')}
                                        value={buttonIconPxDisplayed}
                                        onChange={(v) => {
                                            const n = parseInt(v, 10);
                                            const px = Number.isFinite(n) ? Math.min(48, Math.max(12, n)) : buttonIconAutoPx();
                                            update('buttonIconSizePx', px);
                                        }}
                                        min={12}
                                        max={48}
                                    />
                                    {content.buttonIconSizePx != null && content.buttonIconSizePx >= 12 && (
                                        <button
                                            type="button"
                                            onClick={() => update('buttonIconSizePx', undefined)}
                                            style={{ marginTop: '6px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '0.8rem' }}
                                        >
                                            {t('builder:buttonStyledIconSizeAuto')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'Typography' && (
                        <div>
                            <div className="bc-form-group">
                                <label className="bc-label">Font weight</label>
                                <select className="bc-input" value={content.fontWeight} onChange={e => update('fontWeight', e.target.value)}>
                                    <option>Global</option>
                                    <option>Light</option>
                                    <option>Normal</option>
                                    <option>Medium</option>
                                    <option>Semi-bold</option>
                                    <option>Bold</option>
                                </select>
                            </div>
                            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                <SliderControl label="Space b/w chars" value={content.letterSpacing} onChange={v => update('letterSpacing', v)} max={20} />
                                <SliderControl label="Space b/w words" value={content.wordSpacing} onChange={v => update('wordSpacing', v)} max={30} />
                            </div>
                            <ToggleSwitch label="Uppercase" value={content.uppercase} onChange={v => update('uppercase', v)} />
                        </div>
                    )}

                    {activeTab === 'Colors' && (
                        <div>
                            <CollapsibleSection id="NORMAL" title="Normal" expandedId={expandedColors} onToggle={setExpandedColors}>
                                <ColorRow label="Color (Text)" value={content.colorsNormal.text} onChange={v => updateNormalColor('text', v)} />
                                <ColorRow label="Background color" value={content.colorsNormal.bg} onChange={v => updateNormalColor('bg', v)} />
                                <ColorRow label="Border color" value={content.colorsNormal.border} onChange={v => updateNormalColor('border', v)} />
                                {content.showIcon && (
                                    <div className="bc-form-group" style={{ marginTop: '8px' }}>
                                        <ColorRow
                                            label={t('builder:buttonStyledIconColor')}
                                            value={content.iconColor || content.colorsNormal?.text || '#ffffff'}
                                            onChange={(v) => update('iconColor', v)}
                                        />
                                        {String(content.iconColor || '').trim() !== '' && (
                                            <button
                                                type="button"
                                                onClick={() => update('iconColor', '')}
                                                style={{ marginTop: '6px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '0.8rem' }}
                                            >
                                                {t('builder:buttonStyledIconColorInherit')}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </CollapsibleSection>

                            <CollapsibleSection id="HOVER" title="Hover / Focus / Active" expandedId={expandedColors} onToggle={setExpandedColors}>
                                <ColorRow label="Color (Text)" value={content.colorsHover.text} onChange={v => updateHoverColor('text', v)} />
                                <ColorRow label="Background color" value={content.colorsHover.bg} onChange={v => updateHoverColor('bg', v)} />
                                <ColorRow label="Border color" value={content.colorsHover.border} onChange={v => updateHoverColor('border', v)} />
                            </CollapsibleSection>
                        </div>
                    )}

                    {activeTab === 'Style' && (
                        <div>
                            <CollapsibleSection id="button-mobile" title={t('builder:mobileLayoutSectionTitle', { defaultValue: 'Ajustes mobile' })} expandedId={expandedStyle} onToggle={setExpandedStyle}>
                                <LayoutMobileOverridesPanel
                                    embedded
                                    variant="widget"
                                    content={content}
                                    onContentChange={triggerChange}
                                />
                            </CollapsibleSection>
                            <CollapsibleSection id="margins" title="Margens" expandedId={expandedStyle} onToggle={setExpandedStyle}>
                                <SliderControl label="Margin left" value={content.marginObj.left} onChange={v => updateMargin('left', v)} min={-150} max={200} />
                                <SliderControl label="Margin right" value={content.marginObj.right} onChange={v => updateMargin('right', v)} min={-150} max={200} />
                                <SliderControl label="Margin top" value={content.marginObj.top} onChange={v => updateMargin('top', v)} min={-150} max={200} />
                                <SliderControl label="Margin bottom" value={content.marginObj.bottom} onChange={v => updateMargin('bottom', v)} min={-150} max={200} />
                            </CollapsibleSection>

                            <CollapsibleSection id="cssClass" title="CSS Customizado" expandedId={expandedStyle} onToggle={setExpandedStyle}>
                                <div className="bc-form-group">
                                    <label className="bc-label">Custom css class</label>
                                    <input type="text" className="bc-input" value={content.customClass} onChange={e => update('customClass', e.target.value)} />
                                    <span className="bc-hint">Use this field to add a custom css class and then refer to it in your css style.</span>
                                </div>
                            </CollapsibleSection>
                        </div>
                    )}
                </div>

                <ColorPaletteDock />
                {/* FOOTER */}
                <div className="bc-footer">
                    <button className="bc-btn-cancel" onClick={handleCancel}>{t('builder:cancel')}</button>
                    <button className="bc-btn-save" onClick={() => onSave(content)}>{t('builder:save')}</button>
                </div>
                {/* Handle de resize - arrastar pelo canto */}
                <div className="bc-resize-handle" onPointerDown={handleResizeStart} title={t('builder:resizeModalHint')} aria-hidden />
            </div>
        </div>
    );
    return createPortal(modalContent, document.body);
};

export default ButtonConfigModal;
